/**
 * Web IPC adapter for opentool-web mode.
 *
 * Supports two deployment patterns:
 * 1. Same-origin: opentool-web serves both UI and API (default)
 * 2. Split-origin: UI on GitHub Pages, API on localhost:9900 (--backend flag)
 *
 * When running under wry (native), window.ipc.postMessage exists natively.
 * When running as a web app, this adapter:
 * 1. Overrides window.ipc.postMessage to POST to the API
 * 2. Subscribes to SSE for push events
 * 3. Dispatches events using the same CustomEvent system the native mode uses
 */

// Window globals are declared in bootstrap.ts — no redeclaration needed here.

let eventSource: EventSource | null = null;
let apiBase = '';  // same-origin by default

/**
 * Resolve the API base URL.
 * - Same-origin (opentool-web serves everything): ''
 * - GitHub Pages / split-origin: 'http://localhost:9900'
 * - Custom: ?backend=http://host:port query param
 */
function resolveApiBase(): string {
  // Check URL query param first
  const params = new URLSearchParams(window.location.search);
  const custom = params.get('backend');
  if (custom) return custom.replace(/\/$/, '');

  // If we're on GitHub Pages or a non-localhost origin, default to localhost:9900
  const host = window.location.hostname;
  if (host.endsWith('.github.io') || host.endsWith('.pages.dev') || host.endsWith('.netlify.app')) {
    return 'http://localhost:9900';
  }

  // Same-origin
  return '';
}

export function installWebIpc(): void {
  if (window.ipc) return;

  apiBase = resolveApiBase();
  console.log(`[opentool-web] IPC adapter → ${apiBase || 'same-origin'}`);

  const ipcObj: any = {
    __web: true,
    postMessage(message: string) {
      fetch(`${apiBase}/api/ipc`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: message,
      }).catch((err) => console.warn('[opentool-web] IPC send failed:', err));
    },
  };
  window.ipc = ipcObj;

  connectSSE();
}

function connectSSE(): void {
  if (eventSource) eventSource.close();

  eventSource = new EventSource(`${apiBase}/api/events`);

  const events = [
    'navigate', 'agent-text', 'agent-phase', 'agent-done',
    'agent-error', 'agent-tool', 'agent-usage', 'agent-tool-result',
    'ipc', 'bootstrap', 'warning',
  ];

  for (const name of events) {
    eventSource.addEventListener(name, (e) => {
      try {
        const data = JSON.parse((e as MessageEvent).data);
        if (name === 'bootstrap') {
          (window as any).__OPENTOOL_BOOTSTRAP__ = data;
        }
        window.dispatchEvent(new CustomEvent(`opentool-${name}`, { detail: data }));
      } catch {
        // non-JSON event data
        window.dispatchEvent(new CustomEvent(`opentool-${name}`, { detail: (e as MessageEvent).data }));
      }
    });
  }

  eventSource.onerror = () => {
    console.warn('[opentool-web] SSE disconnected, reconnecting in 2s...');
    eventSource?.close();
    setTimeout(connectSSE, 2000);
  };
}

/**
 * Check if running in web mode and install adapter if so.
 */
export async function initWebMode(): Promise<boolean> {
  if (window.ipc) return false;

  apiBase = resolveApiBase();

  try {
    const res = await fetch(`${apiBase}/api/status`);
    if (res.ok) {
      const data = await res.json();
      installWebIpc();

      (window as any).__OPENTOOL_BOOTSTRAP__ = {
        route: 'home',
        projectName: data.project_root?.split('/').pop() ?? 'workspace',
        payload: null,
      };

      return true;
    }
  } catch {
    // Backend not running — show connection UI
    console.warn(`[opentool-web] Backend not reachable at ${apiBase || 'same-origin'}`);
  }

  return false;
}
